import { useSendOTP, useSignup, useVerifyOtherOTP } from "@/hooks/use-auth";
import { Users, Lock, X, User, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner"
import { useAuth } from "./auth-provider";
import { clearPendingPhone, setPendingPhone } from "@/lib/auth";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { cn } from "@/lib/utils";


type UserInfo = { firstname: string; lastname: string; mobile: string };

type UserModalProp = {
    setAppointerUser : (u:any)=>void,
    setUserModalOpen : (m: boolean) => void,
    setSummaryOpen : (m: boolean) => void
}

export default function UserModal({setAppointerUser, setUserModalOpen, setSummaryOpen} : UserModalProp) {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState<string>("");
    const signupMutation = useSignup();
    const [countryCode] = useState("+251")
    const { t, i18n } = useTranslation();
    const { user } = useAuth()
    const sendOTPMutation = useSendOTP()
    const [verificationSent, setVerificationSent] = useState(false);
    const [otp, setOtp] = useState<string>(""); // concatenated otp
    const verifyOtherOTPMutation = useVerifyOtherOTP();
    const [isResending, setIsResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);



    const otpInputs = useRef<Array<HTMLInputElement | null>>([]);





    useEffect(() => {
        if (resendCooldown <= 0) return;
        const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(id);
    }, [resendCooldown]);





    function isPhoneSameAsUser(phone: string) {
        if (!user) return false
        if (phone.length == 10 && phone[0] === '0') {
            phone = phone.slice(1)
        }
        const formatedPhone = String("+251" + phone).replace(/\D/g, "")
        // try common user phone fields
        const uphones = [
            (user as any).mobile,

        ]
        return uphones.some((p) => !!p && String(p).replace(/\D/g, "") === formatedPhone)
    }


    function focusAt(index: number) {
        const el = otpInputs.current[index];
        el?.focus();
        el?.select();
    }




    async function handleVerify(e: React.FormEvent) {
        e.preventDefault()
        if (otp.length !== 5 || !phone) {
            toast.error(t("Please enter a valid 5-digit OTP"))
            return
        }

        try {
            const result = await verifyOtherOTPMutation.mutateAsync({ mobile: phone, otp })

            // If user and token are returned, user is registered
            if (result.message && result.status === "success") {
                clearPendingPhone()
                setAppointerUser({ mobile: phone, firstname: firstName, lastname: lastName })
                setUserModalOpen(false);
                setSummaryOpen(true);
            }
        } catch (error: any) {
            // If 401, user is not registered - redirect to signup

            toast.error(error.message || t("Invalid OTP. Please try again."))

        }
    }


    async function resendOtp() {
        if (isResending || resendCooldown > 0) return;
        const mobile = phone.trim();
        if (!mobile) { toast.error(t("Mobile number required")); return; }
        
        setIsResending(true);
        try {
            const normalized = `${countryCode}${mobile.replace(/\D/g, "")}`
            await sendOTPMutation.mutateAsync({ mobile: normalized })
            toast.success(t("OTP resent successfully"))
            setResendCooldown(60);
        } catch (error: any) {
            toast.error(error.message || t("Failed to resend OTP"))
        } finally {
            setIsResending(false);
        }
    }








    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (!phone || !firstName.trim() || !lastName.trim()) {
            toast.error(t("Please fill all fields"))
            return
        }

        const cleanPhone = phone.replace(/\D/g, "")

        if (cleanPhone.length === 9 && !cleanPhone.startsWith("9")) {
            toast.error(t("Only Ethio Telecom numbers are supported"))
            return
        }

        if (cleanPhone.length === 10 && !cleanPhone.startsWith("09")) {
            toast.error(t("Only Ethio Telecom numbers are supported"))
            return
        }

        if (cleanPhone.length < 9 || cleanPhone.length > 10) {
            toast.error(t("Please enter a valid phone number"))
            return
        }

        if (isPhoneSameAsUser(cleanPhone)) {
            toast.error(t("You cannot use your own phone number for the 'Other' booking"))
            return
        }

        const normalized = `${countryCode}${cleanPhone.startsWith("0") ? cleanPhone.substring(1) : cleanPhone}`

        try {
            await sendOTPMutation.mutateAsync({ mobile: normalized })
            setPendingPhone(normalized)
            toast.success(t("OTP sent successfully"))
            setVerificationSent(true);
            setResendCooldown(60);
            setOtp("");
            // clear inputs values
            otpInputs.current.forEach((inp) => { if (inp) inp.value = ""; });
            focusAt(0);
        } catch (error: any) {
            toast.error(error.message || t("Failed to send OTP"))
        }


    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={() => setUserModalOpen(false)}>
            {/* Backdrop with blur */}
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-in fade-in duration-300" />
            
            {/* Modal Card */}
            <div 
                className="relative w-full max-w-md bg-white rounded-[2rem] shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300" 
                onClick={(e) => e.stopPropagation()}
            >
                {/* Decorative Header Background */}
                
                {/* Close Button */}
                <button 
                    onClick={() => setUserModalOpen(false)}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-all z-20"
                >
                    <X size={20} />
                </button>

                <div className="relative z-10 px-5 py-8 sm:px-8 sm:py-12">
                    {!verificationSent ? (
                        // Step 1: User Info
                        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                             {/* Header */}
                             <div className="flex flex-col items-center text-center mb-6 sm:mb-8">
                                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white shadow-lg shadow-pink-100 flex items-center justify-center mb-3 sm:mb-4 ring-4 ring-pink-50">
                                    <Users className="w-6 h-6 sm:w-8 sm:h-8 text-pink-500" />
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t("Booking for Someone Else")}</h2>
                                <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-xs">
                                    {t("Please enter the details of the person who will be attending the appointment.")}
                                </p>
                             </div>

                             <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                                {/* Inputs with Icons */}
                                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-700 ml-1">{t("firstName")}</label>
                                        <div className="relative">
                                            <User className="absolute left-3.5 top-3 sm:top-3.5 w-4 h-4 text-gray-400" />
                                            <input 
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                className="w-full h-10 sm:h-11 pl-10 pr-4 rounded-xl border border-gray-200 bg-gray-50/50 text-sm focus:bg-white focus:border-pink-500 focus:ring-4 focus:ring-pink-500/10 transition-all outline-none"
                                                placeholder={t("firstName")}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-gray-700 ml-1">{t("lastName")}</label>
                                        <div className="relative">
                                            <User className="absolute left-3.5 top-3 sm:top-3.5 w-4 h-4 text-gray-400" />
                                            <input 
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                className="w-full h-10 sm:h-11 pl-10 pr-4 rounded-xl border border-gray-200 bg-gray-50/50 text-sm focus:bg-white focus:border-pink-500 focus:ring-4 focus:ring-pink-500/10 transition-all outline-none"
                                                placeholder={t("lastName")}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-gray-700 ml-1">{t("MobileNumber")}</label>
                                    <div className="flex gap-3">
                                        <div className="flex shrink-0 items-center justify-center w-20 h-11 sm:h-12 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 font-bold text-sm">
                                            🇪🇹 +251
                                        </div>
                                        <div className="relative flex-1 min-w-0">
                                            <Phone className="absolute left-3.5 top-3 sm:top-3.5 w-4 h-4 text-gray-400" />
                                            <input 
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                className="w-full h-11 sm:h-12 pl-10 pr-4 rounded-xl border border-gray-200 bg-gray-50/50 text-sm focus:bg-white focus:border-pink-500 focus:ring-4 focus:ring-pink-500/10 transition-all outline-none font-medium"
                                                placeholder="9XXXXXXXX"
                                                inputMode="numeric"
                                                maxLength={10}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-gray-400 ml-1">{t("Enter without country code (e.g., 911223344)")}</p>
                                </div>

                                <button
                                    type="submit"
                                    disabled={sendOTPMutation.isPending}
                                    className="w-full h-11 sm:h-12 rounded-full bg-pink-600 text-white text-sm font-bold shadow-lg shadow-pink-200 hover:bg-pink-700 hover:shadow-pink-300 hover:-translate-y-0.5 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
                                >
                                    {sendOTPMutation.isPending && (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    )}
                                    {sendOTPMutation.isPending ? t("sending") : t("sendVerificationCode")}
                                </button>

                                <p className="text-[10px] text-gray-500 text-center px-6">
                                    {t("By continuing, you agree to our")} <span className="text-pink-600 font-semibold">{t("Terms of Service")}</span> {t("and")}
                                    <span className="text-pink-600 font-semibold"> {t("Privacy Policy")}</span>
                                </p>
                             </form>
                        </div>
                    ) : (
                        // Step 2: OTP
                        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="flex flex-col items-center text-center mb-6 sm:mb-8">
                                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white shadow-lg shadow-pink-100 flex items-center justify-center mb-3 sm:mb-4 ring-4 ring-pink-50">
                                    <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-pink-500" />
                                </div>
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900">{t("Verify Guest's Number")}</h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    {t("We've sent a verification code to")} <br/>
                                    <span className="text-pink-600 font-bold text-base">{countryCode} {phone}</span>
                                </p>
                            </div>

                            <form onSubmit={handleVerify} className="space-y-5 sm:space-y-6">
                                <div className="flex justify-center">
                                    <InputOTP
                                        autoFocus
                                        maxLength={5}
                                        value={otp}
                                        onChange={setOtp}
                                        disabled={verifyOtherOTPMutation.isPending}
                                    >
                                        <InputOTPGroup className="gap-2 sm:gap-3">
                                            {[0, 1, 2, 3, 4].map((i) => (
                                                <InputOTPSlot
                                                    key={i}
                                                    index={i}
                                                    className={cn(
                                                        "w-10 h-12 sm:w-14 sm:h-16 rounded-xl border-2 text-lg sm:text-xl font-bold transition-all duration-200 bg-gray-50",
                                                        "focus:border-pink-500 focus:ring-4 focus:ring-pink-500/10 focus:bg-white focus:scale-105",
                                                        "data-[active=true]:border-pink-500 data-[active=true]:bg-white data-[active=true]:scale-105",
                                                        otp[i] ? "border-pink-500 bg-pink-50/30 text-pink-600" : "border-gray-100 text-gray-900"
                                                    )}
                                                />
                                            ))}
                                        </InputOTPGroup>
                                    </InputOTP>
                                </div>
                                
                                <div className="flex flex-col items-center gap-4">
                                    <button
                                        type="submit"
                                        disabled={verifyOtherOTPMutation.isPending || otp.length !== 5}
                                        className="w-full h-12 rounded-full bg-pink-600 text-white text-sm font-bold shadow-lg shadow-pink-200 hover:bg-pink-700 hover:shadow-pink-300 hover:-translate-y-0.5 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
                                    >
                                        {verifyOtherOTPMutation.isPending && (
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        )}
                                        {verifyOtherOTPMutation.isPending ? t("verifying") : t("verify")}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={resendOtp}
                                        disabled={isResending || resendCooldown > 0}
                                        className={cn(
                                            "text-xs font-semibold transition-colors py-2 px-4 rounded-full hover:bg-gray-50",
                                            isResending || resendCooldown > 0
                                                ? "text-gray-400 cursor-not-allowed"
                                                : "text-pink-600 hover:text-pink-700"
                                        )}
                                    >
                                        {isResending ? (
                                            <span className="flex items-center gap-2">
                                                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                {t("Resending...")}
                                            </span>
                                        ) : resendCooldown > 0 ? (
                                            <span>{t("Resend in")} {resendCooldown}s</span>
                                        ) : (
                                            t("Resend Code")
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
